import React from 'react';
import { Reveal, SectionHead, IconTile } from './UI';
import SplitText from './SplitText';
import {
  HoverSlider,
  TextStaggerHover,
  HoverSliderContentWrap,
  HoverSliderContent
} from './ui/animated-slideshow';

const PILLARS = [
  {
    num: '01',
    tag: 'Operations',
    icon: <polyline points="3,12 8,12 10,5 14,19 16,12 21,12" />,
    heading: 'Live Floor Visibility',
    title: "Catch problems while they're happening",
    body: "Catch idle machines, bottlenecks and productivity loss while they're happening, not at month-end. TruVue AI keeps a live eye on floor activity across every zone and shift.",
    stat: { label: 'Idle time flagged', value: 'within 30s' },
    footnote: 'No new sensors to install — TruVue AI reads directly off the video feed you already have running on the floor.',
  },
  {
    num: '02',
    tag: 'Compliance',
    icon: (
      <>
        <path d="M12 2 20 5.2v6C20 16.6 16.7 20 12 22 7.3 20 4 16.6 4 11.2v-6z" />
        <path d="M8.6 12.2 11 14.6 15.6 10" />
      </>
    ),
    heading: 'Audit-Ready Logs',
    title: 'Auto-log violations, audit-ready',
    body: 'Auto-log PPE, SOP and hygiene violations with timestamped, searchable evidence. Hand an auditor a complete record on any day — no scrambling, no gaps.',
    stat: { label: 'Evidence retained', value: '100% timestamped' },
    footnote: 'Every log is exportable in one click, formatted the way OSHA and internal auditors expect to see it.',
  },
  {
    num: '03',
    tag: 'Security',
    icon: (
      <>
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </>
    ),
    heading: 'Instant Breach Alerts',
    title: 'Flag breaches as they occur',
    body: 'Flag unauthorised access, theft and perimeter breaches as they occur, not after the loss report. TruVue AI connects directly to your existing camera network — no new hardware.',
    stat: { label: 'New hardware needed', value: 'none' },
    footnote: 'Alerts route straight to the right person\'s phone — security lead, shift supervisor, or both — the moment something looks wrong.',
  },
];

export default function Platform() {
  return (
    <section id="platform" className="section section-cream">
      <style>{`
        #platform [class*="eyebrow"]::before,
        #platform [class*="eyebrow"]::after {
          content: none !important;
          display: none !important;
        }
        #platform .platform-container {
          max-width: 1520px;
          margin: 0 auto;
          padding-inline: clamp(12px, 2vw, 20px);
        }
        #platform .platform-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(24px, 5vw, 64px);
          align-items: start;
          margin-top: 60px;
        }
        #platform .platform-left-col {
          display: flex;
          flex-direction: column;
          gap: 20px;
          align-items: flex-start;
          position: sticky;
          top: clamp(90px, 12vh, 140px);
          align-self: start;
        }
        #platform .platform-heading-item {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: clamp(32px, 4vw, 56px);
          line-height: 1.1;
          color: var(--ink);
          margin: 0;
        }
        #platform .platform-right-col {
          height: auto;
        }
        #platform .platform-inner-card {
          background: linear-gradient(120deg, #FDFBFF, #F3EEFB, #E8DFF7, #F3EEFB, #FDFBFF);
          background-size: 300% 300%;
          animation: platformGradientMove 10s ease infinite;
          border-radius: 18px;
          padding: clamp(26px, 3vw, 36px);
          width: 100%;
          border: 2px solid #111111;
          box-shadow: 0 20px 50px rgba(0,0,0,0.12);
          display: flex;
          flex-direction: column;
        }
        @keyframes platformGradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        #platform .platform-footnote {
          margin-top: 24px;
          padding-top: 18px;
          border-top: 1px solid var(--border-cream);
          background: none;
        }
        #platform .platform-footnote p {
          color: var(--ink-soft);
          font-size: 15px;
          line-height: 1.65;
          margin: 0;
        }
        @media (max-width: 860px) {
          #platform .platform-row {
            grid-template-columns: 1fr;
          }
          #platform .platform-left-col {
            position: static;
          }
        }
      `}</style>

      <div className="platform-container">
        <Reveal>
          <SectionHead
            center
            eyebrow="Meet TruVue AI"
            h2="One layer of intelligence across your whole site"
            p="TruVue AI sits on top of the cameras you already have and turns them into a live operations, compliance and security layer — watching every feed, flagging what matters, and keeping a clear, time-stamped record of what happened and when."
          />
        </Reveal>

        <HoverSlider className="platform-row">
          {/* LEFT — Interactive Stagger Headings */}
          <div className="platform-left-col">
            {PILLARS.map((p, i) => (
              <TextStaggerHover
                key={p.num}
                index={i}
                className="platform-heading-item"
                text={p.heading}
              />
            ))}
          </div>

          {/* RIGHT — Cards with clip-path transition */}
          <HoverSliderContentWrap className="platform-right-col">
            {PILLARS.map((p, i) => (
              <HoverSliderContent key={p.num} index={i}>
                <div className="platform-inner-card">
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 18,
                  }}>
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '.08em',
                      color: 'var(--heat)',
                      background: 'var(--heat-light)',
                      padding: '5px 12px',
                      borderRadius: 6,
                      border: '1.5px solid var(--heat)',
                    }}>{p.tag}</span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 22,
                        fontWeight: 700,
                        color: 'var(--ink)',
                        lineHeight: 1,
                      }}>{p.num}</span>
                      <IconTile>{p.icon}</IconTile>
                    </div>
                  </div>

                  <h3 style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 400,
                    fontSize: 'clamp(20px, 2vw, 25px)',
                    margin: '16px 0 10px',
                    color: 'var(--ink)',
                  }}>{p.title}</h3>

                  <p style={{
                    color: 'var(--ink-soft)',
                    fontSize: 15,
                    lineHeight: 1.65,
                    marginBottom: 20,
                  }}>{p.body}</p>

                  <div style={{
                    borderTop: '1px solid var(--border-cream)',
                    paddingTop: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--ink-faint)' }}>{p.stat.label}</span>
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                      fontSize: 15,
                      color: 'var(--heat)',
                    }}>{p.stat.value}</span>
                  </div>

                  <div className="platform-footnote">
                    <p>{p.footnote}</p>
                  </div>
                </div>
              </HoverSliderContent>
            ))}
          </HoverSliderContentWrap>
        </HoverSlider>
      </div>
    </section>
  );
}